import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { ArrowUp } from 'lucide-react';

export function Layout({
  children,
  currentView,
  onViewChange,
  user,
  onSignOut,
  onOpenMyProfile,
  recentContactsCount = 0,
  selectedDepartments = [],
  selectedOffices = [],
  selectedSmartGroup,
  // App.jsx has passed these since the smart-group feature landed; Layout never
  // destructured them, so the five scopes had no sidebar UI at all — their only
  // route in was a radio list buried in FilterDrawer below Sort.
  smartGroups = [],
  scopesLoading = false,
  onSelectSmartGroup,
  savedFilters = [],
  onApplySavedFilter,
  activeSavedFilterId = null,
  isAdmin = false,
  showDepartments = true,
  showExpertise = true,
  showOrgChart = true,
  showRecentContacts = true,
  showGrowth = true,
  showSmartGroups = true,
  searchQuery = '',
  onSearchChange,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchPopoutOpen, setSearchPopoutOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState(searchQuery);
  const searchInputRef = useRef(null);
  const searchPopoutRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const mainRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => { setLocalSearchValue(searchQuery); }, [searchQuery]);

  // Directory + anniversaries pages can run long (a tall anniversary month
  // stretches the whole scroll area), so surface a jump-to-top shortcut once
  // the reader has scrolled past roughly one screen of content.
  useEffect(() => {
    setShowBackToTop(false);
  }, [currentView]);

  const handleMainScroll = (e) => {
    setShowBackToTop(e.currentTarget.scrollTop > 480);
  };

  const scrollToTop = () => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setLocalSearchValue(v);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => onSearchChange?.(v), 300);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (localSearchValue) {
        setLocalSearchValue('');
        onSearchChange?.('');
      } else {
        setSearchPopoutOpen(false);
      }
    }
  };

  // Popout auto-focuses its input on open, and closes on an outside click —
  // same pattern as the user menu flyout.
  useEffect(() => {
    if (searchPopoutOpen) {
      const id = setTimeout(() => { searchInputRef.current?.focus(); searchInputRef.current?.select(); }, 50);
      return () => clearTimeout(id);
    }
  }, [searchPopoutOpen]);

  useEffect(() => {
    if (!searchPopoutOpen) return;
    const handle = (e) => {
      if (searchPopoutRef.current && !searchPopoutRef.current.contains(e.target)) setSearchPopoutOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [searchPopoutOpen]);

  // Global shortcuts to jump into search from anywhere in the app, same as
  // the old page-header search input offered.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault(); setSearchPopoutOpen(true);
      } else if (e.key === '/') {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag !== 'input' && tag !== 'textarea' && !document.activeElement?.isContentEditable) {
          e.preventDefault(); setSearchPopoutOpen(true);
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return localStorage.getItem('peoplevue_dark_mode') === 'true'; } catch { return false; }
  });
  const [moreOpen, setMoreOpen] = useState(false);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    if (next) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    try { localStorage.setItem('peoplevue_dark_mode', String(next)); } catch { /* storage unavailable (private mode) */ }
  };

  // Sync on mount in case localStorage had dark mode set
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const footerRef = useRef(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const handle = (e) => {
      if (footerRef.current && !footerRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [userMenuOpen]);

  const handleViewChange = (id) => {
    onViewChange(id);
    setMobileOpen(false);
    setMoreOpen(false);
  };

  const mainViews = [
    { id: 'grid', label: 'Directory', icon: PeopleIcon },
    showDepartments && { id: 'department', label: 'Departments', icon: DepartmentIcon },
    showExpertise && { id: 'expertise', label: 'Expertise', icon: SparklesIcon },
    showOrgChart && { id: 'orgchart', label: 'Org Chart', icon: OrgChartIcon },
    showGrowth && { id: 'growth', label: 'Growth', icon: GrowthIcon },
  ].filter(Boolean);

  const workspaceViews = [
    showRecentContacts && { id: 'recent', label: 'Recent Contacts', icon: ClockIcon, count: recentContactsCount },
  ].filter(Boolean);

  const totalActiveFilters =
    selectedDepartments.length + selectedOffices.length + (selectedSmartGroup ? 1 : 0);

  const navLabelClass = 'text-[10px] font-semibold text-[#587471] dark:text-slate-600 uppercase tracking-widest px-3 pb-2';
  const navDividerClass = 'my-3 mx-1 border-t border-[#DEE8E7] dark:border-white/[0.05]';

  const sidebarContent = (
    <div className="flex flex-col h-full w-full bg-[#ECF3F2] dark:bg-[#0c0c0d]">
      {/* Logo */}
      <div className="flex-shrink-0 flex flex-col justify-center h-20 px-4 border-b border-[#D3E4E2] dark:border-white/[0.07]">
        <SidebarLogo />
      </div>

      {/* Search — a row+popout (not a persistent input) so it matches
          BusinessDevVUE's Search/New/Home pattern: click opens a panel that
          flies out to the right of the sidebar, same as the user menu. */}
      <div className="flex-shrink-0 px-2 pt-3 pb-1">
        <div className="relative" ref={searchPopoutRef}>
          <AnimatePresence>
            {searchPopoutOpen && (
              <motion.div
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -6 }}
                transition={{ duration: 0.15 }}
                className="absolute left-full top-0 ml-4 w-80 rounded-xl bg-surface border border-line overflow-hidden z-50 shadow-e3"
              >
                <div className="relative p-4">
                  <SearchIcon className="absolute left-7 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-4 pointer-events-none" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={localSearchValue}
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search people…"
                    className="w-full h-11 rounded-xl pl-9 pr-8 text-[15px] outline-none
                               bg-surface border border-accent-line text-ink placeholder-ink-4
                               focus:border-accent transition-colors"
                  />
                  {localSearchValue && (
                    <button
                      onClick={() => { setLocalSearchValue(''); onSearchChange?.(''); searchInputRef.current?.focus(); }}
                      aria-label="Clear search"
                      className="absolute right-6 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink-2"
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                {localSearchValue.trim().length < 2 && (
                  <>
                    <div className="border-t border-line" />
                    <p className="text-sm text-ink-4 text-center py-4 px-4">Type at least 2 characters to search</p>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => setSearchPopoutOpen((o) => !o)}
            className={`w-full flex items-center gap-3 rounded-md px-3 py-2 min-h-9 text-sm transition-colors
              text-[#0f766e] dark:text-teal-400 hover:bg-[#E0EBEA] dark:hover:bg-slate-700
              ${searchPopoutOpen ? 'bg-[#0f766e]/12 dark:bg-slate-700' : ''}`}
          >
            <SearchIcon className="w-[18px] h-[18px] flex-shrink-0" />
            <span className="flex-1 text-left truncate">
              {searchQuery ? `Search: “${searchQuery}”` : 'Search'}
            </span>
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto px-2 py-3 min-h-0">
        {/* VIEWS */}
        <p className={navLabelClass}>Views</p>
        <div className="space-y-1">
          {mainViews.map((view) => (
            <NavButton
              key={view.id}
              view={view}
              isActive={currentView === view.id}
              onClick={() => handleViewChange(view.id)}
            />
          ))}
        </div>

        {(workspaceViews.length > 0 || (showSmartGroups && (scopesLoading || smartGroups.length > 0))) && (
          <div className={navDividerClass} />
        )}

        {workspaceViews.length > 0 && (
          <>
            {/* MY WORKSPACE */}
            <p className={navLabelClass}>My Workspace</p>
            <div className="space-y-1">
              {workspaceViews.map((view) => (
                <NavButton
                  key={view.id}
                  view={view}
                  isActive={currentView === view.id}
                  onClick={() => handleViewChange(view.id)}
                />
              ))}
            </div>
          </>
        )}

        {/* Scoped views. A zero-member scope renders disabled rather than being
            dropped, so the section does not silently change length when
            currentUser resolves. */}
        {/* Mockup 6B: five fixed shimmer slots hold the slot while currentUser is
            resolved out of 739 records, so the sidebar cannot change length when
            the scopes land. */}
        {showSmartGroups && scopesLoading && (
          <>
            <p className={`${navLabelClass} pt-4`}>Quick Selections</p>
            <div className="py-0.5" aria-hidden>
              {[64, 52, 72, 58, 46].map((width, i) => (
                <div
                  key={i}
                  className="h-[9px] rounded-[5px] mx-2.5 my-[11px] sidebar-shimmer"
                  style={{ width: `${width}%` }}
                />
              ))}
            </div>
            <p className="px-3 pb-2.5 text-[10.5px] leading-[1.5] text-[#587471] dark:text-slate-600">
              Working out your team from the directory…
            </p>
          </>
        )}

        {showSmartGroups && !scopesLoading && smartGroups.length > 0 && onSelectSmartGroup && (
          <>
            <p className={`${navLabelClass} pt-4`}>Quick Selections</p>
            <div className="space-y-1">
              {smartGroups.map((group) => {
                const active = selectedSmartGroup === group.id;
                const empty = !group.count;
                return (
                  <button
                    key={group.id}
                    onClick={() => { if (!empty) { onSelectSmartGroup(active ? null : group.id); handleViewChange('grid'); } }}
                    disabled={empty}
                    // Mockup 6A: disabled WITH a reason. A greyed row that says
                    // nothing reads as broken rather than as "not applicable to you".
                    title={empty ? `${group.name} — ${group.reason ?? 'nobody matches this scope for you'}` : undefined}
                    aria-pressed={active}
                    className={`w-full flex items-center gap-3 rounded-md px-3 py-2 min-h-9 text-sm transition-colors
                      ${empty ? 'text-[#587471] dark:text-slate-600 cursor-default'
                        : active
                          ? 'bg-[#0f766e]/12 text-[#0f766e] dark:bg-slate-700 dark:text-white'
                          : 'text-[#3B5452] dark:text-slate-300 hover:bg-[#E0EBEA] hover:text-[#0f766e] dark:hover:bg-slate-700 dark:hover:text-teal-400'}`}
                  >
                    <span className={`flex-none w-1.5 h-1.5 rounded-full ${active ? 'bg-[#0f766e] dark:bg-teal-400' : 'bg-current opacity-40'}`} />
                    <span className="font-medium truncate flex-1 text-left">{group.name}</span>
                    <span className="font-mono text-[10.5px] tabular-nums opacity-70">{group.count}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Saved views — saveCurrentFilter/applyFilter already existed in
            useSearch but were reachable only from inside SearchBar. */}
        {savedFilters.length > 0 && onApplySavedFilter && (
          <>
            <p className={`${navLabelClass} pt-4`}>Saved Views</p>
            <div className="space-y-1">
              {savedFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => { onApplySavedFilter(filter); handleViewChange('grid'); }}
                  aria-pressed={activeSavedFilterId === filter.id}
                  className={`w-full flex items-center gap-3 rounded-md px-3 py-2 min-h-9 text-sm transition-colors
                    ${activeSavedFilterId === filter.id
                      ? 'bg-[#0f766e]/12 text-[#0f766e] dark:bg-slate-700 dark:text-white'
                      : 'text-[#3B5452] dark:text-slate-300 hover:bg-[#E0EBEA] hover:text-[#0f766e] dark:hover:bg-slate-700 dark:hover:text-teal-400'}`}
                >
                  <BookmarkIcon />
                  <span className="font-medium truncate flex-1 text-left">{filter.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

      </nav>

      {/* ── Footer ─────────────────────────────────────── */}
      <div className="flex-shrink-0">
        {/* User row + menu */}
        {user && (
          <div className="p-2 border-t border-[#D3E4E2] dark:border-white/[0.07]">
          <div className="relative" ref={footerRef}>
            {/* Dropdown menu — flies out to the right of the sidebar, bottom-
                aligned to the user button itself (not the credit line below
                it), so it never overlaps the row that opened it */}
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute left-full bottom-0 ml-4 w-60 rounded-xl bg-surface border border-line overflow-hidden z-50 shadow-e3"
                >
                  {onOpenMyProfile && (
                    <button
                      onClick={() => { setUserMenuOpen(false); onOpenMyProfile(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-ink-2 hover:bg-surface-2 transition-colors border-b border-line"
                    >
                      <UserIcon className="w-4 h-4 flex-shrink-0 text-ink-3" />
                      My profile
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => { handleViewChange('settings'); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-ink-2 hover:bg-surface-2 transition-colors border-b border-line"
                    >
                      <SettingsIcon className="w-4 h-4 flex-shrink-0 text-ink-3" />
                      Settings
                    </button>
                  )}
                  <button
                    onClick={toggleDarkMode}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3 text-sm text-ink-2 hover:bg-surface-2 transition-colors border-b border-line"
                  >
                    <span className="flex items-center gap-3">
                      <DarkModeIcon className="w-4 h-4 flex-shrink-0 text-ink-3" />
                      {isDarkMode ? 'Dark mode' : 'Light mode'}
                    </span>
                    <ThemeToggle isDarkMode={isDarkMode} />
                  </button>
                  <button
                    onClick={() => { setUserMenuOpen(false); onSignOut(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  >
                    <SignOutIcon className="w-4 h-4 flex-shrink-0" />
                    Sign out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* User row button */}
            <button
              onClick={() => setUserMenuOpen((o) => !o)}
              className="w-full flex items-center gap-2.5 px-2 py-2 rounded-md hover:bg-[#E0EBEA] dark:hover:bg-slate-700 transition-colors"
            >
              {user.photo ? (
                <img src={user.photo} alt={user.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0 ring-2 ring-[#D3E4E2] dark:ring-white/10" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[#E0EBEA] dark:bg-slate-700 flex items-center justify-center text-[#3B5452] dark:text-slate-300 font-semibold text-sm flex-shrink-0 ring-2 ring-[#D3E4E2] dark:ring-white/10">
                  {user.name?.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-[#3B5452] dark:text-white truncate">{user.name}</p>
                <p className="text-[11px] text-[#587471] dark:text-slate-500 truncate">{user.username}</p>
              </div>
              <svg
                className="w-3 h-3 text-[#587471] dark:text-slate-500 flex-shrink-0"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

            {/* Credit line */}
            <p className="text-[10px] text-[#587471] dark:text-slate-600 text-left pt-2 pb-1 px-2 leading-[1.6]">
              Made with{' '}
              <span className="text-teal-400">♥</span>
              {' '}by<br />MOREgroup Solutions Development<br />© 2026 All Rights Reserved
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-canvas overflow-hidden text-ink">
      {/* Mobile backdrop */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        style={{ width: '210px' }}
        className={`
          fixed lg:relative inset-y-0 left-0 z-40 flex flex-col h-screen flex-shrink-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        aria-label="Primary navigation"
      >
        <div className="relative flex h-full flex-col">
          {sidebarContent}
          {/* Gradient accent border — 0.5px hairline, matching BusinessDevVUE */}
          <div className="pointer-events-none absolute inset-y-0 right-0 w-[0.5px] bg-gradient-to-b from-teal-500 to-cyan-500 dark:from-teal-400 dark:to-cyan-400 dark:opacity-50" />
        </div>
      </aside>

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="lg:hidden flex items-center h-14 px-4 bg-surface border-b border-line flex-shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="relative p-2 -ml-1 mr-2 rounded-xl text-ink-3 hover:bg-surface-2 transition-colors active:scale-95"
          >
            <HamburgerIcon className="w-5 h-5" />
            {totalActiveFilters > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-accent rounded-full text-[9px] text-[var(--on-accent)] font-bold flex items-center justify-center">
                {totalActiveFilters}
              </span>
            )}
          </button>

          <div className="flex items-center gap-2 flex-1">
            <span className="text-base font-black text-ink" style={{ fontFamily: "'Outfit', sans-serif" }}>
              People<span className="text-accent">VUE</span>
            </span>
          </div>

          {user && (
            <button
              onClick={() => onOpenMyProfile?.()}
              aria-label="Open my profile"
              className="w-8 h-8 rounded-full ring-2 ring-accent/30 overflow-hidden flex-shrink-0 active:scale-95 transition-transform"
            >
              {user.photo ? (
                <img src={user.photo} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-accent to-accent-ink flex items-center justify-center text-[var(--on-accent)] text-[11px] font-bold">
                  {user.name?.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
              )}
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <main
          ref={mainRef}
          onScroll={currentView === 'orgchart' ? undefined : handleMainScroll}
          className={`flex-1 pb-20 lg:pb-0 ${currentView === 'orgchart' ? 'overflow-hidden' : 'overflow-y-auto [scrollbar-gutter:stable]'}`}
        >
          {currentView === 'orgchart' ? (
            <div className="h-full overflow-hidden">
              {children}
            </div>
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {children}
            </div>
          )}

          {showBackToTop && currentView !== 'orgchart' && (
            <button
              type="button"
              onClick={scrollToTop}
              aria-label="Back to top"
              title="Back to top"
              className="fixed bottom-6 right-6 z-30 w-10 h-10 rounded-full flex items-center justify-center
                         bg-surface border border-line-strong shadow-e2 text-ink-2
                         hover:text-accent-ink hover:border-accent transition-colors"
            >
              <ArrowUp size={17} />
            </button>
          )}
        </main>
      </div>

      {/* ── Mobile Bottom Navigation ───────────────────────── */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface/95 backdrop-blur-xl border-t border-line"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex h-14">
          <BottomNavButton icon={PeopleIcon} label="Directory" active={currentView === 'grid'} onClick={() => handleViewChange('grid')} />
          {showDepartments && <BottomNavButton icon={DepartmentIcon} label="Depts" active={currentView === 'department'} onClick={() => handleViewChange('department')} />}
          {showRecentContacts && <BottomNavButton icon={ClockIcon} label="Recent" active={currentView === 'recent'} badge={recentContactsCount} onClick={() => handleViewChange('recent')} />}
          <BottomNavButton
            icon={MoreIcon}
            label="More"
            active={['orgchart', 'expertise', 'growth', 'settings'].includes(currentView) || moreOpen}
            onClick={() => { setMobileOpen(false); setMoreOpen(true); }}
          />
        </div>
      </nav>

      {/* ── More Bottom Sheet ──────────────────────────────── */}
      <AnimatePresence>
        {moreOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-[60]"
              onClick={() => setMoreOpen(false)}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 32, stiffness: 380 }}
              className="lg:hidden fixed bottom-0 inset-x-0 z-[70] bg-surface rounded-t-3xl overflow-hidden border-t border-line shadow-e3"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-line rounded-full" />
              </div>

              <div className="px-4 pt-1 pb-2">
                <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider px-4 mb-1">Views</p>

                {showOrgChart && (
                  <button
                    onClick={() => handleViewChange('orgchart')}
                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-colors
                      ${currentView === 'orgchart' ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2'}`}
                  >
                    <OrgChartIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Org Chart</span>
                  </button>
                )}

                {showExpertise && (
                  <button
                    onClick={() => handleViewChange('expertise')}
                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-colors
                      ${currentView === 'expertise' ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2'}`}
                  >
                    <SparklesIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Expertise</span>
                  </button>
                )}

                {showGrowth && (
                  <button
                    onClick={() => handleViewChange('growth')}
                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-colors
                      ${currentView === 'growth' ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2'}`}
                  >
                    <GrowthIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Growth</span>
                  </button>
                )}

                {isAdmin && (
                  <button
                    onClick={() => handleViewChange('settings')}
                    className={`w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-colors
                      ${currentView === 'settings' ? 'bg-accent-soft text-accent' : 'text-ink-2 hover:bg-surface-2'}`}
                  >
                    <SettingsIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium">Settings</span>
                  </button>
                )}

                <div className="my-3 border-t border-line" />

                <p className="text-[10px] font-semibold text-ink-3 uppercase tracking-wider px-4 mb-1">Account</p>

                {onOpenMyProfile && (
                  <button
                    onClick={() => { setMoreOpen(false); onOpenMyProfile(); }}
                    className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-ink-2 hover:bg-surface-2 transition-colors"
                  >
                    <UserIcon className="w-5 h-5 flex-shrink-0 text-ink-3" />
                    <span className="font-medium">My Profile</span>
                  </button>
                )}

                <button
                  onClick={() => { toggleDarkMode(); setMoreOpen(false); }}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3.5 rounded-2xl text-ink-2 hover:bg-surface-2 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <DarkModeIcon className="w-5 h-5 flex-shrink-0 text-ink-3" />
                    <span className="font-medium">{isDarkMode ? 'Dark Mode' : 'Light Mode'}</span>
                  </div>
                  <ThemeToggle isDarkMode={isDarkMode} />
                </button>

                <button
                  onClick={() => { setMoreOpen(false); onSignOut(); }}
                  className="w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl text-red-500 hover:bg-red-50/80 dark:hover:bg-red-950/30 transition-colors"
                >
                  <SignOutIcon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium">Sign Out</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/**
 * The wordmark + sub-logo lockup, kept as one unit so the two pieces can't
 * drift apart under future edits. The exact arrangement was reverse-engineered
 * pixel-by-pixel against the brand reference: "by MOREgroup" starts under the
 * second lowercase "p" in "People" and ends flush with the "E" in "VUE".
 *
 * Two non-obvious things this depends on:
 *
 * 1. The wrapper is `w-fit`, not `inline-block`. This div is a flex item (its
 *    parent is `flex flex-col`), and CSS "blockifies" an inline-level box
 *    that's a flex item — `display: inline-block` silently becomes `block`,
 *    which stretches the wrapper to the flex container's full width instead
 *    of shrink-wrapping to the wordmark. `w-fit` (`width: fit-content`) isn't
 *    subject to that rule, so it actually shrink-wraps. Without this, the
 *    sub-logo's `ml-auto` right-aligns against the wrong (much wider) box and
 *    the whole thing silently drifts out of position.
 *
 * 2. The sub-logo is a `mask-image` div, not an `<img>`. The source PNG has
 *    real alpha transparency but two different ink colors baked in (dark
 *    "MORE", light-gray "group") — masking it lets a single background-color
 *    fill both letterforms through the PNG's alpha channel, so "by MOREgroup"
 *    renders in the exact same color as "People" instead of its own colors.
 */
function SidebarLogo() {
  return (
    <div className="w-fit">
      <h1 className="text-[34px] font-bold whitespace-nowrap text-[#3B5452] dark:text-white leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
        People<span className="bg-gradient-to-r from-teal-500 to-cyan-500 dark:from-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">VUE</span>
      </h1>
      <div
        role="img"
        aria-label="by MOREgroup"
        className="block ml-auto w-[6.5rem] aspect-[456/64] -mt-1 bg-[#3B5452] dark:bg-white"
        style={{
          WebkitMaskImage: 'url(/byMOREgroup.PNG)',
          maskImage: 'url(/byMOREgroup.PNG)',
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'right center',
          maskPosition: 'right center',
        }}
      />
    </div>
  );
}

function NavButton({ view, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 rounded-lg pl-4 pr-3 py-2 min-h-9 text-sm font-medium transition-colors
        ${isActive
          ? 'bg-accent-soft text-accent-ink'
          : 'text-[#3B5452] dark:text-slate-300 hover:bg-surface-2 hover:text-accent-ink'
        }`}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-1 top-1/2 -translate-y-1/2 h-[55%] w-[3px] rounded-full bg-accent"
        />
      )}
      <view.icon className="w-[18px] h-[18px] flex-shrink-0" />
      <span className="flex-1 text-left truncate">{view.label}</span>
      {view.count > 0 && (
        <span className="px-2 py-0.5 text-[11px] rounded-full bg-[#E0EBEA] text-[#3B5452] dark:bg-slate-600 dark:text-slate-200">
          {view.count}
        </span>
      )}
    </button>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────


function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PeopleIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function DepartmentIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  );
}

function OrgChartIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
    </svg>
  );
}

function BookmarkIcon({ className = 'w-3.5 h-3.5 flex-none' }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-4-7 4V5z" />
    </svg>
  );
}

function ClockIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function HamburgerIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function SignOutIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function DarkModeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function SunIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

/** Sun/moon toggle track — both icons always visible, thumb slides between them. */
/**
 * The active mode's icon rides inside the moving thumb (large, colored); the
 * other mode's icon stays put in the track (small, faint) — the icon itself
 * is what communicates state, not just the thumb position.
 */
function ThemeToggle({ isDarkMode }) {
  return (
    <span className={`relative w-10 h-[22px] rounded-full flex-shrink-0 bg-gradient-to-r transition-colors ${isDarkMode ? 'from-amber-300 to-orange-500' : 'from-sky-300 to-blue-500'}`}>
      <SunIcon className="absolute left-[5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/60" />
      <DarkModeIcon className="absolute right-[5px] top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-white/60" />
      <motion.span
        animate={{ x: isDarkMode ? 18 : 0 }}
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute left-0.5 top-0.5 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white shadow-sm"
      >
        {isDarkMode ? (
          <DarkModeIcon className="w-3 h-3 text-orange-500" />
        ) : (
          <SunIcon className="w-3 h-3 text-amber-400" />
        )}
      </motion.span>
    </span>
  );
}


function SettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function UserIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function SparklesIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.7a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-2a2 2 0 0 0 1.3-1.3L12 3z" />
    </svg>
  );
}

function GrowthIcon({ className }) {
  // Sprout — growth/coaching section
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 20h10" />
      <path d="M12 20v-9" />
      <path d="M12 11C12 6 8 4 4 4c0 4 2 7 8 7z" />
      <path d="M12 8c0-3 3-5 7-5 0 4-2 6-7 6" />
    </svg>
  );
}

function MoreIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}

function BottomNavButton({ icon: Icon, label, active, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex flex-col items-center justify-center gap-[3px] relative transition-colors
        ${active ? 'text-accent' : 'text-ink-3'}`}
    >
      {active && (
        <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-[2px] bg-accent rounded-full" />
      )}
      <div className="relative">
        <Icon className="w-[22px] h-[22px]" />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 bg-accent rounded-full text-[9px] text-[var(--on-accent)] font-bold flex items-center justify-center px-1 leading-none">
            {badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium leading-none">{label}</span>
    </button>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────

export function LoginScreen({ onSignIn, isLoading, error }) {
  return (
    <div
      className="relative min-h-screen flex items-center justify-center p-4 overflow-hidden"
      style={{ backgroundColor: '#111113' }}
    >
      {/* Ambient teal glow behind card */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#2dd4bf]/8 rounded-full blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-[340px]"
      >
        {/* Border wrapper */}
        <div className="relative">
          <div
            className="p-[2px] rounded-2xl"
            style={{ background: 'linear-gradient(180deg, #5eead4 0%, #0d9488 100%)' }}
          >
          <div className="rounded-2xl px-8 py-9" style={{ backgroundColor: '#1c1c1e' }}>

            {/* Logo */}
            <div className="inline-block mb-8" style={{ fontSize: '2.35rem' }}>
              <h1 className="font-bold text-white leading-[0.9] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontSize: '1em' }}>People<span className="text-[#5eead4]">VUE</span></h1>
              <img
                src="/byMOREgroup_drk.png"
                alt="by MOREgroup"
                className="block ml-auto w-[58%] object-contain brightness-0 invert"
                style={{ marginTop: '0.14em' }}
              />
            </div>

            <p className="text-white/40 text-sm mb-7">
              Sign in to access the employee directory.
            </p>

            {error && (
              <div className="mb-5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}

            {/* Sign in button — gradient matching accent strip */}
            <button
              onClick={onSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 px-6 py-3.5 rounded-xl
                         font-medium text-white text-sm transition-opacity
                         bg-gradient-to-r from-[#5eead4] to-[#0d9488]
                         hover:opacity-90 active:opacity-80
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Signing in…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 21 21" fill="currentColor">
                    <path d="M0 0h10v10H0V0zm11 0h10v10H11V0zM0 11h10v10H0V11zm11 0h10v10H11V11z" />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>

            <p className="mt-6 text-[10px] text-white/25 text-center leading-relaxed">
              Made with{' '}
              <span className="bg-gradient-to-r from-[#5eead4] to-[#0d9488] bg-clip-text text-transparent">
                ♥
              </span>
              {' '}by MOREgroup Solutions Development
            </p>
          </div>
        </div>
        </div>
      </motion.div>
    </div>
  );
}
